import { httpClient } from '../api/http-client';

export interface Permission {
  id: string;
  nombre: string;
  descripcion?: string;
  activo: boolean;
  creadoEn: string;
  actualizadoEn: string;
}

export interface Module {
  id: string;
  nombre: string;
  descripcion?: string;
  ruta?: string;
  icono?: string;
  orden: number;
  tipo: 'navigation' | 'drawer';
  moduloPadreId?: string;
  activo: boolean;
  creadoEn: string;
  actualizadoEn: string;
  permisos?: Permission[];
}

export interface CreateModuleDto {
  nombre: string;
  descripcion?: string;
  ruta?: string;
  icono?: string;
  orden?: number;
  tipo?: 'navigation' | 'drawer';
  moduloPadreId?: string;
  activo?: boolean;
}

export interface UpdateModuleDto {
  nombre?: string;
  descripcion?: string;
  ruta?: string;
  icono?: string;
  orden?: number;
  tipo?: 'navigation' | 'drawer';
  moduloPadreId?: string;
  activo?: boolean;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
}

export interface PaginatedModulesResponse {
  data: Module[];
  meta: PaginationMeta;
}

// Cache temporal para permisos de módulos (hasta que se implemente en backend)
const modulePermissionsCache = new Map<string, Permission[]>();

export class ModulesService {
  static async getModules(
    page: number = 1, 
    limit: number = 10, 
    activo?: boolean
  ): Promise<PaginatedModulesResponse> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });

      if (activo !== undefined) {
        params.append('activo', activo.toString());
      }

      const response = await httpClient.get<PaginatedModulesResponse>(
        `/auth/modules?${params}`
      );
      
      return response.data || { data: [], meta: { page: 1, limit: 10, total: 0, totalPages: 0, hasPrevious: false, hasNext: false } };
    } catch (error) {
      console.error('Error fetching modules:', error);
      return { data: [], meta: { page: 1, limit: 10, total: 0, totalPages: 0, hasPrevious: false, hasNext: false } };
    }
  }

  static async searchModules(
    searchTerm: string,
    page: number = 1, 
    limit: number = 10, 
    activo?: boolean
  ): Promise<PaginatedModulesResponse> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        search: searchTerm
      });

      if (activo !== undefined) {
        params.append('activo', activo.toString());
      }

      const response = await httpClient.get<PaginatedModulesResponse>(
        `/auth/modules?${params}`
      );
      
      return response.data || { data: [], meta: { page: 1, limit: 10, total: 0, totalPages: 0, hasPrevious: false, hasNext: false } };
    } catch (error) {
      console.error('Error searching modules:', error);
      return { data: [], meta: { page: 1, limit: 10, total: 0, totalPages: 0, hasPrevious: false, hasNext: false } };
    }
  }

  static async getModuleById(id: string): Promise<Module> {
    const response = await httpClient.get<Module>(
      `/auth/modules/${id}`
    );
    
    return response.data;
  }

  static async createModule(moduleData: CreateModuleDto): Promise<Module> {
    const response = await httpClient.post<Module>(
      '/auth/modules',
      moduleData
    );
    
    return response.data;
  }

  static async updateModule(id: string, moduleData: UpdateModuleDto): Promise<Module> {
    const response = await httpClient.put<Module>(
      `/auth/modules/${id}`,
      moduleData
    );
    
    return response.data;
  }

  static async deleteModule(id: string): Promise<void> {
    await httpClient.delete(`/auth/modules/${id}`);
  }

  static async toggleModuleStatus(id: string): Promise<Module> {
    const response = await httpClient.post<Module>(
      `/auth/modules/${id}/toggle-status`
    );
    
    return response.data;
  }

  static async getAllModules(): Promise<Module[]> {
    const response = await httpClient.get<Module[]>(
      '/auth/modules/all'
    );
    
    return response.data;
  }

  static async getModuleWithPermissions(id: string): Promise<Module> {
    // Por ahora, obtenemos el módulo base y luego intentamos obtener permisos
    // desde el endpoint general de permisos
    const module = await this.getModuleById(id);
    
    // Intentar obtener permisos desde el endpoint general
    try {
      const permissionsResponse = await httpClient.get<{ modulos: Array<{ nombre: string; ruta: string | null; permisos: string[] }> }>(
        '/auth/permissions'
      );
      
      const modulePermissions = permissionsResponse.data.modulos.find(
        m => m.nombre === module.nombre
      );
      
      if (modulePermissions) {
        // Convertir strings a objetos Permission
        const backendPermissions = modulePermissions.permisos.map((permiso) => ({
          id: `${module.id}_${permiso}`,
          nombre: permiso,
          descripcion: `Permiso de ${permiso} para ${module.nombre}`,
          activo: true,
          creadoEn: new Date().toISOString(),
          actualizadoEn: new Date().toISOString()
        }));
        
        // Usar permisos en caché si existen, sino usar los del backend
        module.permisos = modulePermissionsCache.get(module.id) || backendPermissions;
        
        // Guardar en caché si no existían
        if (!modulePermissionsCache.has(module.id)) {
          modulePermissionsCache.set(module.id, backendPermissions);
        }
      }
      
      return module;
    } catch (error) {
      // Si falla, devolver el módulo sin permisos
      return module;
    }
  }

  static async getAllPermissions(): Promise<Permission[]> {
    const response = await httpClient.get<{ modulos: Array<{ nombre: string; ruta: string | null; permisos: string[] }> }>(
      '/auth/permissions'
    );
    
    // Crear un set único de todos los permisos disponibles
    const uniquePermissions = new Set<string>();
    response.data.modulos.forEach(modulo => {
      modulo.permisos.forEach(permiso => uniquePermissions.add(permiso));
    });
    
    // Convertir a objetos Permission
    return Array.from(uniquePermissions).map((permiso) => ({
      id: `permission_${permiso}`,
      nombre: permiso,
      descripcion: `Permiso de ${permiso}`,
      activo: true,
      creadoEn: new Date().toISOString(),
      actualizadoEn: new Date().toISOString()
    }));
  }

  static async addPermissionToModule(moduleId: string, permissionId: string): Promise<Module> {
    // Por ahora, simulamos la adición del permiso localmente
    // TODO: Implementar endpoint en el backend
    console.warn('addPermissionToModule: Endpoint not implemented, simulating locally');
    
    try {
      const module = await this.getModuleWithPermissions(moduleId);
      
      // Extraer el nombre del permiso del ID
      const permissionName = permissionId.replace('permission_', '');
      
      // Obtener permisos actuales de la caché
      const currentPermissions = modulePermissionsCache.get(moduleId) || module.permisos || [];
      
      // Verificar si el permiso ya existe
      const existingPermission = currentPermissions.find(p => p.nombre === permissionName);
      if (existingPermission) {
        return module; // Ya existe, no hacer nada
      }
      
      // Agregar el nuevo permiso
      const newPermission: Permission = {
        id: `${moduleId}_${permissionName}`,
        nombre: permissionName,
        descripcion: `Permiso de ${permissionName} para ${module.nombre}`,
        activo: true,
        creadoEn: new Date().toISOString(),
        actualizadoEn: new Date().toISOString()
      };
      
      const updatedPermissions = [...currentPermissions, newPermission];
      
      // Actualizar la caché
      modulePermissionsCache.set(moduleId, updatedPermissions);
      
      // Actualizar el módulo
      module.permisos = updatedPermissions;
      return module;
      
    } catch (error) {
      throw new Error('No se pudo agregar el permiso al módulo');
    }
  }

  static async removePermissionFromModule(moduleId: string, permissionId: string): Promise<Module> {
    // Por ahora, simulamos la remoción del permiso localmente
    // TODO: Implementar endpoint en el backend
    console.warn('removePermissionFromModule: Endpoint not implemented, simulating locally');
    
    try {
      const module = await this.getModuleWithPermissions(moduleId);
      
      // Obtener permisos actuales de la caché
      const currentPermissions = modulePermissionsCache.get(moduleId) || module.permisos || [];
      
      // Remover el permiso de la lista
      const updatedPermissions = currentPermissions.filter(p => p.id !== permissionId);
      
      // Actualizar la caché
      modulePermissionsCache.set(moduleId, updatedPermissions);
      
      // Actualizar el módulo
      module.permisos = updatedPermissions;
      return module;
      
    } catch (error) {
      throw new Error('No se pudo remover el permiso del módulo');
    }
  }
}
